import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

interface WeekData {
  week: number;
  topic: string;
  lessons: string[];
  practiceTest?: string;
  hours: string;
  focus: string;
}

interface StudyTrack {
  id: string;
  name: string;
  duration: string;
  description: string;
  bestFor: string;
  weeks: WeekData[];
}

const studyTracks: StudyTrack[] = [
  {
    id: 'intensive',
    name: '8-Week Intensive',
    duration: '8 weeks',
    description: 'Fast-paced preparation for motivated students',
    bestFor: 'Students with strong foundation who need focused review',
    weeks: [
      {
        week: 1,
        topic: 'Algebraic Expressions & Equations',
        lessons: ['Simplifying expressions', 'Solving linear equations', 'Multi-step equations'],
        hours: '6-8 hours',
        focus: 'Master equation solving techniques'
      },
      {
        week: 2,
        topic: 'Functions & Relations',
        lessons: ['Function notation', 'Domain and range', 'Function operations'],
        practiceTest: 'Functions Quiz',
        hours: '6-8 hours',
        focus: 'Understand function concepts deeply'
      },
      {
        week: 3,
        topic: 'Linear Functions & Systems',
        lessons: ['Slope and y-intercept', 'Writing equations', 'Systems of equations'],
        hours: '7-9 hours',
        focus: 'Graph and solve linear systems'
      },
      {
        week: 4,
        topic: 'Quadratic Functions',
        lessons: ['Factoring', 'Quadratic formula', 'Graphing parabolas'],
        practiceTest: 'Mid-Point Practice Test',
        hours: '7-9 hours',
        focus: 'Master all quadratic solving methods'
      },
      {
        week: 5,
        topic: 'Exponential Functions & Statistics',
        lessons: ['Exponential growth/decay', 'Data analysis', 'Regression'],
        hours: '6-8 hours',
        focus: 'Apply exponential models'
      },
      {
        week: 6,
        topic: 'Polynomials & Radicals',
        lessons: ['Polynomial operations', 'Simplifying radicals', 'Rational expressions'],
        practiceTest: 'Full Practice Exam 1',
        hours: '7-9 hours',
        focus: 'Complete all polynomial operations'
      },
      {
        week: 7,
        topic: 'Comprehensive Review',
        lessons: ['All topics review', 'Common mistakes', 'Test strategies'],
        practiceTest: 'Full Practice Exam 2',
        hours: '8-10 hours',
        focus: 'Identify and fix weak areas'
      },
      {
        week: 8,
        topic: 'Final Preparation',
        lessons: ['Timed practice', 'Part II-IV strategies', 'Calculator tips'],
        practiceTest: 'Full Practice Exam 3',
        hours: '8-10 hours',
        focus: 'Build test-day confidence'
      }
    ]
  },
  {
    id: 'standard',
    name: '12-Week Standard',
    duration: '12 weeks',
    description: 'Balanced approach with thorough coverage',
    bestFor: 'Most students seeking comprehensive preparation',
    weeks: [
      {
        week: 1,
        topic: 'Algebraic Foundations',
        lessons: ['Order of operations', 'Properties of numbers', 'Simplifying expressions'],
        hours: '4-5 hours',
        focus: 'Build strong algebra basics'
      },
      {
        week: 2,
        topic: 'Linear Equations',
        lessons: ['One-step equations', 'Multi-step equations', 'Equations with variables on both sides'],
        hours: '4-5 hours',
        focus: 'Master equation solving'
      },
      {
        week: 3,
        topic: 'Inequalities & Absolute Value',
        lessons: ['Solving inequalities', 'Compound inequalities', 'Absolute value equations'],
        practiceTest: 'Equations & Inequalities Quiz',
        hours: '4-5 hours',
        focus: 'Understand inequality notation'
      },
      {
        week: 4,
        topic: 'Functions Basics',
        lessons: ['Function notation', 'Evaluating functions', 'Domain and range'],
        hours: '4-5 hours',
        focus: 'Grasp function concepts'
      },
      {
        week: 5,
        topic: 'Linear Functions',
        lessons: ['Slope', 'Writing linear equations', 'Parallel and perpendicular lines'],
        hours: '5-6 hours',
        focus: 'Master linear relationships'
      },
      {
        week: 6,
        topic: 'Systems of Equations',
        lessons: ['Graphing method', 'Substitution', 'Elimination'],
        practiceTest: 'Linear Functions Quiz',
        hours: '5-6 hours',
        focus: 'Solve systems efficiently'
      },
      {
        week: 7,
        topic: 'Quadratic Functions Part 1',
        lessons: ['Factoring', 'Zero product property', 'Graphing basics'],
        hours: '5-6 hours',
        focus: 'Factor all quadratic types'
      },
      {
        week: 8,
        topic: 'Quadratic Functions Part 2',
        lessons: ['Quadratic formula', 'Completing the square', 'Vertex form'],
        practiceTest: 'Mid-Point Practice Test',
        hours: '5-6 hours',
        focus: 'Use all solving methods'
      },
      {
        week: 9,
        topic: 'Exponential Functions',
        lessons: ['Exponential growth', 'Exponential decay', 'Applications'],
        hours: '4-5 hours',
        focus: 'Model real-world scenarios'
      },
      {
        week: 10,
        topic: 'Statistics & Data',
        lessons: ['Data analysis', 'Regression', 'Correlation'],
        practiceTest: 'Full Practice Exam 1',
        hours: '5-6 hours',
        focus: 'Interpret statistical data'
      },
      {
        week: 11,
        topic: 'Polynomials & Radicals',
        lessons: ['Polynomial operations', 'Radicals', 'Rational expressions'],
        practiceTest: 'Full Practice Exam 2',
        hours: '5-6 hours',
        focus: 'Complete all operations'
      },
      {
        week: 12,
        topic: 'Final Review & Practice',
        lessons: ['All topics review', 'Test strategies', 'Timed practice'],
        practiceTest: 'Full Practice Exam 3',
        hours: '6-8 hours',
        focus: 'Perfect test-day performance'
      }
    ]
  },
  {
    id: 'relaxed',
    name: '16-Week Relaxed',
    duration: '16 weeks',
    description: 'Gentle pace with extra practice time',
    bestFor: 'Students who want thorough understanding with less pressure',
    weeks: [
      {
        week: 1,
        topic: 'Number Properties & Operations',
        lessons: ['Order of operations', 'Properties of real numbers'],
        hours: '3-4 hours',
        focus: 'Review fundamental concepts'
      },
      {
        week: 2,
        topic: 'Algebraic Expressions',
        lessons: ['Simplifying expressions', 'Combining like terms'],
        hours: '3-4 hours',
        focus: 'Build expression skills'
      },
      {
        week: 3,
        topic: 'One & Two-Step Equations',
        lessons: ['Basic equation solving', 'Checking solutions'],
        hours: '3-4 hours',
        focus: 'Master simple equations'
      },
      {
        week: 4,
        topic: 'Multi-Step Equations',
        lessons: ['Complex equations', 'Variables on both sides'],
        practiceTest: 'Basic Equations Quiz',
        hours: '3-4 hours',
        focus: 'Solve advanced equations'
      },
      {
        week: 5,
        topic: 'Inequalities',
        lessons: ['Solving inequalities', 'Graphing on number line'],
        hours: '3-4 hours',
        focus: 'Understand inequality rules'
      },
      {
        week: 6,
        topic: 'Absolute Value',
        lessons: ['Absolute value equations', 'Absolute value inequalities'],
        hours: '3-4 hours',
        focus: 'Master absolute value concepts'
      },
      {
        week: 7,
        topic: 'Introduction to Functions',
        lessons: ['Function notation', 'Evaluating functions'],
        practiceTest: 'Equations Review Quiz',
        hours: '3-4 hours',
        focus: 'Understand function basics'
      },
      {
        week: 8,
        topic: 'Function Properties',
        lessons: ['Domain and range', 'Function operations'],
        hours: '3-4 hours',
        focus: 'Explore function characteristics'
      },
      {
        week: 9,
        topic: 'Linear Functions Part 1',
        lessons: ['Slope', 'Rate of change'],
        hours: '4-5 hours',
        focus: 'Calculate and interpret slope'
      },
      {
        week: 10,
        topic: 'Linear Functions Part 2',
        lessons: ['Writing equations', 'Point-slope form'],
        practiceTest: 'Functions & Lines Quiz',
        hours: '4-5 hours',
        focus: 'Write linear equations'
      },
      {
        week: 11,
        topic: 'Systems of Equations',
        lessons: ['Graphing systems', 'Substitution method'],
        hours: '4-5 hours',
        focus: 'Solve systems graphically'
      },
      {
        week: 12,
        topic: 'Advanced Systems',
        lessons: ['Elimination method', 'Applications'],
        practiceTest: 'Mid-Point Practice Test',
        hours: '4-5 hours',
        focus: 'Apply all system methods'
      },
      {
        week: 13,
        topic: 'Quadratic Functions',
        lessons: ['Factoring', 'Graphing parabolas'],
        hours: '4-5 hours',
        focus: 'Factor and graph quadratics'
      },
      {
        week: 14,
        topic: 'Exponentials & Statistics',
        lessons: ['Exponential functions', 'Data analysis'],
        practiceTest: 'Full Practice Exam 1',
        hours: '4-5 hours',
        focus: 'Model exponential growth'
      },
      {
        week: 15,
        topic: 'Polynomials & Review',
        lessons: ['Polynomial operations', 'Comprehensive review'],
        practiceTest: 'Full Practice Exam 2',
        hours: '5-6 hours',
        focus: 'Review all major topics'
      },
      {
        week: 16,
        topic: 'Final Preparation',
        lessons: ['Test strategies', 'Timed practice'],
        practiceTest: 'Full Practice Exam 3',
        hours: '5-6 hours',
        focus: 'Build confidence for exam day'
      }
    ]
  }
];

export default function AlgebraRegentsStudyPlanPage() {
  const [selectedTrack, setSelectedTrack] = useState<string>('standard');
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<{ q1: string; q2: string; q3: string }>({
    q1: '',
    q2: '',
    q3: ''
  });
  const [recommendedTrack, setRecommendedTrack] = useState<string | null>(null);
  const [currentWeek, setCurrentWeek] = useState(1);

  const track = studyTracks.find(t => t.id === selectedTrack) || studyTracks[1];

  const handleQuizSubmit = () => {
    const { q1, q2, q3 } = quizAnswers;
    
    // Calculate recommendation based on answers
    let score = 0;
    
    // Q1: Months until exam
    if (q1 === '1-2') score += 3;
    else if (q1 === '3-4') score += 2;
    else if (q1 === '4+') score += 1;
    
    // Q2: Hours per week
    if (q2 === '8+') score += 3;
    else if (q2 === '5-7') score += 2;
    else if (q2 === '3-4') score += 1;
    
    // Q3: Comfort level
    if (q3 === 'confident') score += 3;
    else if (q3 === 'okay') score += 2;
    else if (q3 === 'struggling') score += 1;
    
    // Recommend track based on score
    if (score >= 7) {
      setRecommendedTrack('intensive');
      setSelectedTrack('intensive');
    } else if (score >= 5) {
      setRecommendedTrack('standard');
      setSelectedTrack('standard');
    } else {
      setRecommendedTrack('relaxed');
      setSelectedTrack('relaxed');
    }
    
    setShowQuiz(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <Link 
            to="/grades/algebra-regents"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-6 text-sm font-medium"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Algebra Regents Practice
          </Link>
          
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Algebra Regents
            <span className="block text-emerald-600 mt-2">Study Plan Timeline</span>
          </h1>
          
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Choose your personalized study track and follow a proven week-by-week plan to ace your Algebra Regents exam with confidence
          </p>
          
          <button
            onClick={() => setShowQuiz(true)}
            className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            <i className="ri-questionnaire-line text-xl"></i>
            Which Plan Is Right For Me?
          </button>
        </div>
      </section>

      {/* Quiz Modal */}
      {showQuiz && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-slate-900">Find Your Perfect Study Plan</h2>
              <button
                onClick={() => setShowQuiz(false)}
                className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-all"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <div className="space-y-8">
              {/* Question 1 */}
              <div>
                <label className="block text-lg font-semibold text-slate-900 mb-4">
                  1. How many months until your Algebra Regents exam?
                </label>
                <div className="space-y-3">
                  {[
                    { value: '1-2', label: '1-2 months (exam is soon!)' },
                    { value: '3-4', label: '3-4 months (good timing)' },
                    { value: '4+', label: '4+ months (plenty of time)' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setQuizAnswers({ ...quizAnswers, q1: option.value })}
                      className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all ${
                        quizAnswers.q1 === option.value
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 flex items-center justify-center rounded-full border-2 ${
                          quizAnswers.q1 === option.value
                            ? 'border-emerald-500 bg-emerald-500'
                            : 'border-slate-300'
                        }`}>
                          {quizAnswers.q1 === option.value && (
                            <i className="ri-check-line text-white text-sm"></i>
                          )}
                        </div>
                        <span className="font-medium text-slate-700">{option.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Question 2 */}
              <div>
                <label className="block text-lg font-semibold text-slate-900 mb-4">
                  2. How many hours per week can you dedicate to studying?
                </label>
                <div className="space-y-3">
                  {[
                    { value: '3-4', label: '3-4 hours/week (light schedule)' },
                    { value: '5-7', label: '5-7 hours/week (moderate schedule)' },
                    { value: '8+', label: '8+ hours/week (intensive schedule)' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setQuizAnswers({ ...quizAnswers, q2: option.value })}
                      className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all ${
                        quizAnswers.q2 === option.value
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 flex items-center justify-center rounded-full border-2 ${
                          quizAnswers.q2 === option.value
                            ? 'border-emerald-500 bg-emerald-500'
                            : 'border-slate-300'
                        }`}>
                          {quizAnswers.q2 === option.value && (
                            <i className="ri-check-line text-white text-sm"></i>
                          )}
                        </div>
                        <span className="font-medium text-slate-700">{option.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Question 3 */}
              <div>
                <label className="block text-lg font-semibold text-slate-900 mb-4">
                  3. How comfortable are you with algebra right now?
                </label>
                <div className="space-y-3">
                  {[
                    { value: 'struggling', label: 'Struggling - need to build foundation' },
                    { value: 'okay', label: 'Okay - understand basics, need practice' },
                    { value: 'confident', label: 'Confident - just need focused review' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setQuizAnswers({ ...quizAnswers, q3: option.value })}
                      className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all ${
                        quizAnswers.q3 === option.value
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 flex items-center justify-center rounded-full border-2 ${
                          quizAnswers.q3 === option.value
                            ? 'border-emerald-500 bg-emerald-500'
                            : 'border-slate-300'
                        }`}>
                          {quizAnswers.q3 === option.value && (
                            <i className="ri-check-line text-white text-sm"></i>
                          )}
                        </div>
                        <span className="font-medium text-slate-700">{option.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleQuizSubmit}
                disabled={!quizAnswers.q1 || !quizAnswers.q2 || !quizAnswers.q3}
                className="w-full bg-emerald-600 text-white py-4 rounded-xl font-semibold hover:bg-emerald-700 transition-all disabled:bg-slate-300 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Get My Recommended Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Track Selection */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {recommendedTrack && (
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-6 rounded-2xl mb-8 text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <i className="ri-lightbulb-flash-line text-3xl"></i>
                <h3 className="text-2xl font-bold">Recommended For You</h3>
              </div>
              <p className="text-emerald-50 text-lg">
                Based on your answers, we recommend the <strong>{studyTracks.find(t => t.id === recommendedTrack)?.name}</strong> plan
              </p>
            </div>
          )}
          
          <div className="grid md:grid-cols-3 gap-6">
            {studyTracks.map(t => (
              <button
                key={t.id}
                onClick={() => setSelectedTrack(t.id)}
                className={`text-left p-6 rounded-2xl border-2 transition-all ${
                  selectedTrack === t.id
                    ? 'border-emerald-500 bg-emerald-50 shadow-lg'
                    : 'border-slate-200 bg-white hover:border-emerald-300'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-1">{t.name}</h3>
                    <p className="text-emerald-600 font-semibold">{t.duration}</p>
                  </div>
                  {selectedTrack === t.id && (
                    <div className="w-8 h-8 flex items-center justify-center bg-emerald-500 rounded-full">
                      <i className="ri-check-line text-white"></i>
                    </div>
                  )}
                </div>
                
                <p className="text-slate-600 mb-4">{t.description}</p>
                
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Best For:</p>
                  <p className="text-sm text-slate-600">{t.bestFor}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-8">
            {/* Sticky Progress Sidebar */}
            <div className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-2xl p-6 border-2 border-slate-200 shadow-lg">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Your Progress</h3>
                
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">Week {currentWeek} of {track.weeks.length}</span>
                    <span className="text-sm font-bold text-emerald-600">
                      {Math.round((currentWeek / track.weeks.length) * 100)}%
                    </span>
                  </div>
                  <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                      style={{ width: `${(currentWeek / track.weeks.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {track.weeks.map((week) => (
                    <button
                      key={week.week}
                      onClick={() => setCurrentWeek(week.week)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        currentWeek === week.week
                          ? 'bg-emerald-100 border-2 border-emerald-500'
                          : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                          currentWeek === week.week
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-200 text-slate-600'
                        }`}>
                          {week.week}
                        </div>
                        <span className={`text-sm font-medium ${
                          currentWeek === week.week ? 'text-emerald-700' : 'text-slate-600'
                        }`}>
                          {week.topic}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="flex-1">
              <div className="bg-white rounded-2xl p-8 border-2 border-slate-200 shadow-lg mb-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                  {track.name} Study Timeline
                </h2>
                <p className="text-slate-600">
                  Follow this week-by-week plan to master all Algebra Regents topics
                </p>
              </div>

              <div className="space-y-6">
                {track.weeks.map((week, index) => (
                  <div
                    key={week.week}
                    id={`week-${week.week}`}
                    className={`relative bg-white rounded-2xl p-8 border-2 transition-all ${
                      currentWeek === week.week
                        ? 'border-emerald-500 shadow-xl'
                        : 'border-slate-200 hover:border-emerald-300'
                    }`}
                  >
                    {/* Connecting Line */}
                    {index < track.weeks.length - 1 && (
                      <div className="absolute left-12 top-full w-0.5 h-6 bg-slate-300"></div>
                    )}

                    <div className="flex gap-6">
                      {/* Week Number Badge */}
                      <div className="flex-shrink-0">
                        <div className={`w-16 h-16 flex items-center justify-center rounded-2xl font-bold text-2xl ${
                          currentWeek === week.week
                            ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {week.week}
                        </div>
                      </div>

                      {/* Week Content */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-2">
                              {week.topic}
                            </h3>
                            <p className="text-emerald-600 font-semibold flex items-center gap-2">
                              <i className="ri-time-line"></i>
                              {week.hours}
                            </p>
                          </div>
                          
                          {currentWeek === week.week && (
                            <span className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold whitespace-nowrap">
                              Current Week
                            </span>
                          )}
                        </div>

                        {/* Focus Area */}
                        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg mb-6">
                          <p className="text-sm font-semibold text-amber-900 mb-1">
                            <i className="ri-focus-3-line mr-2"></i>
                            Week Focus
                          </p>
                          <p className="text-amber-800">{week.focus}</p>
                        </div>

                        {/* Lessons */}
                        <div className="mb-6">
                          <h4 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                            <i className="ri-book-open-line text-emerald-600"></i>
                            Recommended Lessons
                          </h4>
                          <ul className="space-y-2">
                            {week.lessons.map((lesson, idx) => (
                              <li key={idx} className="flex items-start gap-3">
                                <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                                  <i className="ri-check-line text-emerald-600 text-sm"></i>
                                </div>
                                <span className="text-slate-700">{lesson}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Practice Test */}
                        {week.practiceTest && (
                          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-semibold text-emerald-700 mb-1">
                                  <i className="ri-file-list-3-line mr-2"></i>
                                  Practice Assessment
                                </p>
                                <p className="text-lg font-bold text-slate-900">{week.practiceTest}</p>
                              </div>
                              <Link
                                to="/grades/algebra-regents"
                                className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-all whitespace-nowrap"
                              >
                                Take Test
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Completion Card */}
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-2xl p-8 mt-8 text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
                  <i className="ri-trophy-line text-5xl"></i>
                </div>
                <h3 className="text-3xl font-bold mb-4">You're Ready for Success!</h3>
                <p className="text-emerald-50 text-lg mb-6 max-w-2xl mx-auto">
                  After completing this {track.duration} study plan, you'll have mastered all Algebra Regents topics and be fully prepared for exam day
                </p>
                <Link
                  to="/grades/algebra-regents"
                  className="inline-flex items-center gap-2 bg-white text-emerald-600 px-8 py-4 rounded-full font-bold hover:bg-emerald-50 transition-all shadow-lg whitespace-nowrap"
                >
                  <i className="ri-file-list-3-line text-xl"></i>
                  Start Practice Tests Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}